import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaArrowLeft, FaHeart, FaQuoteLeft, FaPaperPlane, FaUser, FaClock } from 'react-icons/fa';
import './BlessingPage.css';

const BlessingPage = () => {
  const [blessings, setBlessings] = useState([]);
  const [newBlessing, setNewBlessing] = useState({ author: '', text: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  // 获取祝福语列表
  const fetchBlessings = async () => {
    try {
      const response = await fetch('/api/blessings');
      const result = await response.json();
      if (result.success) {
        setBlessings(result.data);
      }
    } catch (error) {
      console.error('获取祝福语失败:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlessings();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewBlessing(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newBlessing.author.trim() || !newBlessing.text.trim()) {
      alert('请填写昵称和祝福内容！');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const response = await fetch('/api/blessings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: newBlessing.text.trim(),
          author: newBlessing.author.trim()
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        // 重新获取祝福语列表以确保数据同步
        await fetchBlessings();
        setNewBlessing({ author: '', text: '' });
        alert('祝福已成功添加！');
      } else {
        alert(result.message || '提交失败，请重试！');
      }
    } catch (error) {
      console.error('提交祝福失败:', error);
      alert('网络错误，请检查连接后重试！');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      className="blessing-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.8 }}
    >
      {/* 顶部区域 */}
      <div className="blessing-top-section">
        <Link to="/" className="back-btn">
          <FaArrowLeft /> 返回首页
        </Link>
        
        <motion.h1 
          className="blessing-title"
          initial={{ y: -30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
        >
          <FaQuoteLeft className="title-icon" />
          祝福语
          <FaHeart className="title-icon" />
        </motion.h1>
        
        <motion.div 
          className="blessing-counter"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.8 }}
        >
          <span className="counter-text">
            累计收到 <span className="counter-number">{blessings.length}</span> 条祝福
          </span>
        </motion.div>
      </div>

      {/* 波浪分隔 */}
      <div className="blessing-wave-divider">
        <svg viewBox="0 0 600 200" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
          <defs>
            <linearGradient id="blessingWaveGradient" x1="0%" y1="100%" x2="0%" y2="0%">
              <stop offset="0%" style={{stopColor: 'rgba(255, 240, 245, 0.4)', stopOpacity: 1}} />
              <stop offset="30%" style={{stopColor: 'rgba(255, 228, 235, 0.5)', stopOpacity: 1}} />
              <stop offset="70%" style={{stopColor: 'rgba(255, 205, 215, 0.7)', stopOpacity: 1}} />
              <stop offset="100%" style={{stopColor: 'rgba(255, 182, 193, 0.8)', stopOpacity: 1}} />
            </linearGradient>
          </defs>
          <path d="M0,100 Q150,50 300,100 T600,100 L600,200 L0,200 Z" fill="url(#blessingWaveGradient)"/>
        </svg>
      </div>

      {/* 底部区域 */}
      <div className="blessing-bottom-section">
        <div className="blessing-container">
          {/* 祝福列表 */}
          <motion.div 
            className="blessings-list"
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.8 }}
          >
            {loading ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p className="loading-text">正在加载祝福语...</p>
              </div>
            ) : blessings.length === 0 ? (
              <div className="empty-container">
                <p className="empty-text">还没有祝福语，快来留下第一条祝福吧！</p>
              </div>
            ) : (
              blessings.map((blessing, index) => (
              <motion.div
                key={blessing.id}
                className="blessing-item"
                initial={{ x: -50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.8 + index * 0.1, duration: 0.6 }}
              >
                <div className="blessing-emoji">{blessing.emoji}</div>
                <div className="blessing-content">
                  <div className="blessing-header-info">
                    <span className="blessing-author">
                      <FaUser className="author-icon" />
                      {blessing.author}
                    </span>
                    <span className="blessing-time">
                      <FaClock className="time-icon" />
                      {blessing.time}
                    </span>
                  </div>
                  <div className="blessing-text">{blessing.text}</div>
                </div>
              </motion.div>
              ))
            )}
          </motion.div>

          {/* 添加祝福表单 */}
          <motion.div 
            className="blessing-form-container"
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 1.0, duration: 0.8 }}
          >
            <h3 className="form-title">
              <FaHeart className="form-icon" />
              留下你的祝福
            </h3>
            <form onSubmit={handleSubmit} className="blessing-form">
              <div className="form-row">
                <div className="input-group">
                  <FaUser className="input-icon" />
                  <input
                    type="text"
                    name="author"
                    placeholder="请输入你的昵称"
                    value={newBlessing.author}
                    onChange={handleInputChange}
                    className="form-input"
                    maxLength="20"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="input-group">
                  <FaQuoteLeft className="input-icon textarea-icon" />
                  <textarea
                    name="text"
                    placeholder="写下你的美好祝福..."
                    value={newBlessing.text}
                    onChange={handleInputChange}
                    className="form-textarea"
                    maxLength="200"
                    rows="4"
                  />
                </div>
              </div>
              <button 
                type="submit" 
                className={`submit-btn ${isSubmitting ? 'submitting' : ''}`}
                disabled={isSubmitting}
              >
                <FaPaperPlane className="submit-icon" />
                {isSubmitting ? '提交中...' : '发送祝福'}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

export default BlessingPage;


